import { FC, useContext, useEffect, useRef, useState } from 'react';
import {
  availableLanguages,
  getProposalCurrencyOptions,
  proposalSortOptions,
  proposalTemplates,
} from 'constants/constant';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { useSearchParams } from 'react-router-dom';
import { applyProposalCurrency, getFirstProposalProductCurrency, hasProposalProducts, resolveProposalLanguageFromBranch } from 'utils/shared/proposal';
import { MainContext } from 'contexts/MainContext';

interface ProposalCustomizeProps {
  onChange: (params: { template: string; language: string; sort: string }) => void;
  hideWrapper?: boolean;
  config?: {
    template: string;
    language: string;
    sort: string;
    currency?: string;
  };
}

const ProposalCustomize: FC<ProposalCustomizeProps> = ({ onChange, hideWrapper, config }) => {
  const { t } = useTranslation();
  const { user, proposal, setProposal, initSiteData } = useContext(MainContext);
  const [template, setTemplate] = useState('t1');
  const [language, setLanguage] = useState('en');
  const [sort, setSort] = useState('1');
  const [urlParams, setUrlParams] = useSearchParams();
  const [currencyOptions] = useState(() => getProposalCurrencyOptions());
  const [currency, setCurrency] = useState(() => getFirstProposalProductCurrency(proposal));
  const productCurrency = getFirstProposalProductCurrency(proposal);
  const storedCurrency = sessionStorage.getItem('displayCurrencyHotels')?.toLowerCase() || '';
  const proposalProductCount =
    proposal.hotels.length +
    proposal.flights.length +
    proposal.dynamic.length +
    proposal.transfers.length +
    proposal.ilPackages.length +
    (proposal.existingProposal?.listOfProducts?.length || 0);
  const prevProposalProductCount = useRef(proposalProductCount);
  const showCurrency = !!hideWrapper && hasProposalProducts(proposal);

  useEffect(() => {
    if (config) {
      setTemplate(config.template);
      setLanguage(config.language);
      setSort(config.sort);
      if (config.currency) {
        setCurrency(config.currency.toLowerCase());
      }
    } else {
      setLanguage(resolveProposalLanguageFromBranch(user?.branch?.ext_1_pcc));
    }
  }, [config, user?.branch?.ext_1_pcc]);

  useEffect(() => {
    const nextCurrency = (config?.currency || storedCurrency || productCurrency || '').toLowerCase();
    if (nextCurrency) {
      setCurrency(nextCurrency);
    }
  }, [productCurrency, storedCurrency, config?.currency]);

  useEffect(() => {
    if (prevProposalProductCount.current === 0 && proposalProductCount > 0) {
      setCurrency(getFirstProposalProductCurrency(proposal));
    }
    prevProposalProductCount.current = proposalProductCount;
  }, [proposalProductCount, proposal]);

  const onClickSave = async () => {
    const currentParams = Object.fromEntries(urlParams);
    setUrlParams({
      ...currentParams,
      template,
      language,
      sort,
    });
    onChange({ template, language, sort });
  };

  const onChangeTemplate = (template: string) => {
    setTemplate(template);
    onChange({ template, language, sort });
  };

  const onChangeLanguage = (language: string) => {
    setLanguage(language);
    onChange({ template, language, sort });
  };

  const onChangeSort = (sort: string) => {
    setSort(sort);
    onChange({ template, language, sort });
  };

  const onChangeCurrency = async (value: string) => {
    const nextCurrency = value.toLowerCase();
    setCurrency(nextCurrency);
    const updatedProposal = await applyProposalCurrency(proposal, nextCurrency);
    setProposal(updatedProposal);
    sessionStorage.setItem('displayCurrencyHotels', nextCurrency);
  };

  return (
    <div className={hideWrapper ? '' : 'pr-single-config'}>
      <div className="row">
        {!hideWrapper && <div className="col-12 mb-10">{t('proposal.customize')}</div>}
        <div className="col-3">
          <SelectCustom
            options={availableLanguages
              .map((lang) => ({
                label: lang.name,
                value: lang.lang,
                flag: lang.country,
              }))
              .filter((lang) => !(['IT', 'ES', 'GR'].includes(initSiteData?.other?.defaultNationality || '') && lang.value === 'he'))}
            value={language}
            onChange={onChangeLanguage}
            menuPlacement={hideWrapper ? 'top' : 'bottom'}
          />
        </div>
        <div className="col-3">
          <SelectCustom
            options={proposalTemplates}
            value={template}
            onChange={onChangeTemplate}
            needToTranslate={true}
            menuPlacement={'top'}
          />
        </div>
        <div className="col-3">
          <SelectCustom
            options={proposalSortOptions}
            value={sort}
            onChange={onChangeSort}
            needToTranslate={true}
            menuPlacement={'top'}
          />
        </div>
        {showCurrency && (
          <div className="col-3">
            <SelectCustom
              options={currencyOptions}
              value={currency}
              onChange={onChangeCurrency}
              showOnlyIcon
              isDropdown
              menuPlacement={'top'}
            />
          </div>
        )}
        {!hideWrapper && (
          <div className="col-3">
            <Button title={t('save')} onClick={onClickSave} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProposalCustomize;
